import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

import { getCurrentPageContext } from "~/hec/analytics/page-context.client";
import { getCookieValue } from "~/utils/cookies";

import { waitForAnalytics } from "./utils";

/**
 * Fires a Segment `page()` on initial load and on every client-side pathname
 * change. Production (SFRA) fires this via the Segment snippet's trailing
 * `analytics.page()` (org_hec `thirdParty/twilioSegmentPixel.js:4`) on every
 * full-page load; the Workers SPA boots Segment through `AnalyticsBrowser.load()`
 * — which, unlike the copy-paste snippet, does NOT auto-fire an initial page
 * call — so route-change page views must be fired explicitly. Without this, zero
 * Segment Page events fire (SFCC-10630 testing-phase finding).
 *
 * Fires once per PATHNAME transition. Search-param-only changes (PLP filter/sort,
 * search-query refinement) deliberately do NOT re-fire: SFRA updated those grids
 * via AJAX (Search-UpdateGrid — no `page()`), and they already emit their own
 * Segment events (`Product List Filtered` / `Products Searched`). To send a page
 * view per distinct URL instead, add `location.search` to the dep + dedup key.
 *
 * Calls `page()` with no args to mirror the prod snippet — Segment auto-captures
 * path/url/title/referrer at call time, and the URL is already updated by the
 * time this post-commit effect runs.
 *
 * TPA gate: ANALYTICS-03 conservatively suppresses Segment for any TPA-branded
 * session (keyed on the `tpaBranding` affiliate cookie) until the SFCC-10793 TPA
 * session bridge lands. develop factors this into a shared `isTpaSuppressed`
 * helper; this branch predates it, so the check is inlined here — consolidate to
 * `isTpaSuppressed` once develop merges in.
 *
 * Mount once at the app root, alongside `<Segment />` / `<SegmentIdentify />`.
 */
export const SegmentPageView = () => {
  const { pathname } = useLocation();
  const firedFor = useRef<string | null>(null);

  useEffect(() => {
    // ANALYTICS-03 TPA marketing gate (see docstring).
    if (getCookieValue(document.cookie, "tpaBranding")) return;
    // Guard a StrictMode double-invoke / same-path effect re-run. A real
    // A→B→A navigation still re-fires because `pathname` actually changes.
    if (firedFor.current === pathname) return;
    firedFor.current = pathname;

    let cancelled = false;
    void (async () => {
      await waitForAnalytics();
      if (cancelled || !window.analytics) return;
      // COM-585 (martech, 2026-09-26): CADENCE is unchanged — still once per
      // pathname transition, which is already the site-owned navigation rule —
      // and the call gains explicit properties describing the route it
      // belongs to. Event name and category deliberately stay unset: passing a
      // single object argument makes it a properties-only page call, exactly
      // as today.
      //
      // NAMING: bare snake_case, matching every other Segment property in this
      // codebase (`cart_id`, `product_id`, `list_id`, `image_url`,
      // `local_currency`, `category_name` — there is no `hec_`-prefixed
      // property anywhere). `hec_navigation_id` is the one exception, named
      // explicitly by martech as the cross-surface join key.
      //
      // None of these collide with `path` / `url` / `title` / `referrer` /
      // `search`, which analytics.js auto-captures, so nothing Segment already
      // populates is overwritten. When the contract is suppressed this spreads
      // to `{}` and the call is byte-identical to before.
      const context = getCurrentPageContext();
      if (!context) {
        // Contract suppressed — call it with NO arguments, exactly as before
        // COM-585. `page({})` would be a different call signature for Segment
        // to normalize; the flag-off path must be the literal pre-change call,
        // not merely an equivalent one.
        void window.analytics.page();
        return;
      }
      void window.analytics.page({
        hec_navigation_id: context.hec_navigation_id,
        page_location: context.hec_page_location,
        page_title: context.hec_page_title,
        page_type: context.hec_page_type,
        ...(context.hec_previous_page_location
          ? { previous_page_location: context.hec_previous_page_location }
          : {}),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return null;
};
